//@ts-check
import { LICENSING_SELECTION_CHANGE, log } from "./LicensingSelector";

/**
 * UI for "The fonts will be used in service of:” (`license_usage` radios + conditional client-name field).
 * Relays edits through the shared `licensing-selection-change` event.
 *
 * See also:
 * - license_usage_label_controller.js
 */
export class UsageSelector {
  /** @type {HTMLElement} */
  rootElement;

  /** @type {string} */
  ownerType;
  /** @type {string} */
  ownerName;

  constructor(rootElement) {
    //@ts-ignore
    window.usageSelector = this;

    this.rootElement = rootElement;
    this.log = log.extend("UsageSelector");
    this.log("Creating UsageSelector", rootElement);

    this._notifySelectionChanged = this.notifySelectionChanged.bind(this);

    /** @type {HTMLInputElement[]} */
    this.usageRadios = Array.from(
      rootElement.querySelectorAll('input[name="license_usage"]')
    );
    /** @type {HTMLInputElement | null} */
    this.clientNameInput = rootElement.querySelector(
      'input[name="license_usage_client_name"]'
    );

    this._handleSelectionChange = this.handleSelectionChange.bind(this);
    this.usageRadios.forEach((el) =>
      el.addEventListener("change", this._handleSelectionChange)
    );

    this._handleClientNameChange = this.handleClientNameChange.bind(this);
    this._handleClientNameInput = this.handleClientNameInput.bind(this);
    if (this.clientNameInput) {
      this.clientNameInput.addEventListener(
        "change",
        this._handleClientNameChange
      );
      this.clientNameInput.addEventListener(
        "input",
        this._handleClientNameInput
      );
    }

    // The Branding option-groups container is greyed out when the usage selection is
    // incomplete (client selected but no name). Excludes the license-usage group itself.
    /** @type {HTMLElement | null} */
    this.brandingOptionsContainer =
      this.usageRadios[0]?.closest(".branding-options") ?? null;

    this.ownerType = this.getOwnerType();
    this.ownerName = this.getOwnerName();
    // Track empty/non-empty to fire immediately when crossing that boundary.
    this._wasEmpty = this.ownerName.trim() === "";
    this.syncEmptyClass();
    this.syncUsageInvalidClass();
    // Debounce timer for typing.
    this._inputDebounceMs = 1000;
    this._inputDebounceTimer = null;
  }

  disconnect() {
    this.usageRadios.forEach((el) =>
      el.removeEventListener("change", this._handleSelectionChange)
    );
    if (this.clientNameInput) {
      this.clientNameInput.removeEventListener(
        "change",
        this._handleClientNameChange
      );
      this.clientNameInput.removeEventListener(
        "input",
        this._handleClientNameInput
      );
    }
    if (this._inputDebounceTimer) {
      clearTimeout(this._inputDebounceTimer);
      this._inputDebounceTimer = null;
    }
  }

  handleSelectionChange() {
    // NOTE: license_usage_label_controller.js also handles this event: updates labels, focuses input, clears input
    this.ownerType = this.getOwnerType();
    this.ownerName = this.getOwnerName();
    this.syncEmptyClass();
    this.syncUsageInvalidClass();
    this.notifySelectionChanged();
  }

  handleClientNameChange() {
    // Native `change` (blur after edit / Enter): cancel any pending debounce and sync now.
    if (this._inputDebounceTimer) {
      clearTimeout(this._inputDebounceTimer);
      this._inputDebounceTimer = null;
    }
    this.ownerName = this.getOwnerName();
    this._wasEmpty = this.ownerName.trim() === "";
    this.syncEmptyClass();
    this.syncUsageInvalidClass();
    this.notifySelectionChanged();
  }

  // Fires on every keystroke. Debounces by `_inputDebounceMs` so typing only
  // triggers one server sync per pause, except when the value crosses the
  // empty/non-empty boundary (e.g. first character typed or last character
  // deleted) — that case syncs immediately so the related "client name blank"
  // error appears/disappears without waiting for the debounce.
  handleClientNameInput() {
    this.ownerName = this.getOwnerName();
    const isEmpty = this.ownerName.trim() === "";
    this.syncEmptyClass();
    this.syncUsageInvalidClass();

    if (isEmpty !== this._wasEmpty) {
      this._wasEmpty = isEmpty;
      if (this._inputDebounceTimer) {
        clearTimeout(this._inputDebounceTimer);
        this._inputDebounceTimer = null;
      }
      this.notifySelectionChanged();
      return;
    }

    if (this._inputDebounceTimer) clearTimeout(this._inputDebounceTimer);
    this._inputDebounceTimer = setTimeout(() => {
      this._inputDebounceTimer = null;
      this.ownerName = this.getOwnerName();
      this.notifySelectionChanged();
    }, this._inputDebounceMs);
  }

  notifySelectionChanged() {
    this.rootElement.dispatchEvent(
      new CustomEvent(LICENSING_SELECTION_CHANGE, { bubbles: true })
    );
  }

  getOwnerType() {
    const checked = this.usageRadios.find((el) => el.checked);
    if (!checked) return null;
    return checked.value === "me" ? "me" : "client";
  }

  getOwnerName() {
    return this.clientNameInput?.value || "";
  }

  syncEmptyClass() {
    if (!this.clientNameInput) return;
    this.clientNameInput.classList.toggle(
      "error",
      this.clientNameInput.value.trim() === ""
    );
  }

  // True when the usage selection is "complete": either "myself", or "client" + non-empty name.
  isUsageValid() {
    if (this.ownerType === "me") return true;
    if (this.ownerType === "client") return this.ownerName.trim() !== "";
    return false;
  }

  // Toggles `.usage-invalid` on the Branding options container so CSS can grey
  // out the dependent fields.
  syncUsageInvalidClass() {
    if (!this.brandingOptionsContainer) return;
    this.brandingOptionsContainer.classList.toggle(
      "usage-invalid",
      !this.isUsageValid()
    );
  }
}
